import ToolbarButton from "../toolbar/ToolbarButton";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useCurrentSimulationResults, useSimulations } from "../../api/simulations";
import { useSimulation } from "../../contexts/SimulationContext";
import { useSelection } from "../../contexts/SelectionContext";
import { useDate } from "../../contexts/DateContext";
import dayjs from "dayjs";
import { useTimezone } from "../../contexts/TimezoneContext.tsx";
import { useUnitSystem } from "../../hooks/useUnitSystem";
import { detectIgnitionGrid, squareCell } from "../../utils/ignitionGrid";
import RiskFromLegend, { RiskFromLegendVariant } from "./RiskFromLegend";
import RiskFromHoverCard from "./RiskFromHoverCard";
import { useMap } from "../../contexts/MapContext";
import {
  RISK_FROM_CELL_ALPHA,
  RISK_FROM_CLASS_COUNT,
  RISK_FROM_POINT_RADIUS,
  RISK_FROM_RAMP,
  RISK_FROM_SHAPE_ALPHA,
  RISK_FROM_SHAPE_ALPHA_HOVER,
  RiskFromFeatureKind,
  RiskFromMetric,
  hexToRgba,
  riskFromClass,
  riskFromInteractiveLayerIds,
  riskFromLayerStyles,
  riskFromMetric,
  riskFromRampPosition,
} from "./riskFromStyles";

/** Gap between grid cells, as a fraction of the step on each side. */
const CELL_INSET = 0.03;
/** Chip features take ids above every ignition index. */
const CHIP_ID_BASE = 1_000_000;

/** One ignition location after collapsing ensemble members. */
interface DisplayIgnition {
  /** Index of the FIRST instance in `ignitions`, so `ignition-<index>` resolves. */
  index: number;
  ignition: any;
  ha_burnt: number;
  asset_impacts: number;
  count: number;
}

/** Where to hang the "Loaded" chip for an ignition's display geometry. */
const anchorOf = (geom: any, ignition: any): [number, number] | null => {
  const t = geom?.type;
  if (t === "Point") return geom.coordinates;
  if (t === "Polygon" || t === "MultiPolygon" || t === "LineString" || t === "MultiLineString") {
    // The ignition's start point sits inside / on its geometry for every
    // regime kind we produce, and it is what the fire is simulated from.
    if (Number.isFinite(ignition?.start_point_lng) && Number.isFinite(ignition?.start_point_lat)) {
      return [ignition.start_point_lng, ignition.start_point_lat];
    }
    const coords: number[][] = [];
    const walk = (c: any) => {
      if (typeof c?.[0] === "number") coords.push(c);
      else if (Array.isArray(c)) c.forEach(walk);
    };
    walk(geom.coordinates);
    if (coords.length === 0) return null;
    const mid = coords[Math.floor(coords.length / 2)];
    return [mid[0], mid[1]];
  }
  return null;
};

const displayGeometry = (
  ignition: any,
  latStep: number,
  lngStep: number,
  isGridded: boolean
): { geometry: any; kind: RiskFromFeatureKind } | null => {
  const geom = ignition?.start_geometry ?? ignition?.geometry;
  const geomType = geom?.type;

  if (geomType === "LineString" || geomType === "MultiLineString") {
    return { geometry: geom, kind: "line" };
  }
  if (geomType === "Polygon" || geomType === "MultiPolygon") {
    return { geometry: geom, kind: "shape" };
  }

  let lng: number | undefined;
  let lat: number | undefined;
  if (geomType === "Point" && Array.isArray(geom?.coordinates)) {
    [lng, lat] = geom.coordinates as [number, number];
  } else {
    lng = ignition?.start_point_lng;
    lat = ignition?.start_point_lat;
  }
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

  if (!isGridded) {
    return { geometry: { type: "Point" as const, coordinates: [lng, lat] }, kind: "point" };
  }
  // Inset each cell slightly so neighbours read as separate tiles.
  const latHalf = (latStep / 2) * (1 - 2 * CELL_INSET);
  const lngHalf = (lngStep / 2) * (1 - 2 * CELL_INSET);
  return { geometry: squareCell(lat!, lng!, latHalf, lngHalf), kind: "cell" };
};

const RiskFromLayer = (id: string, name: string) => {
  const [geojson, setGeojson] = useState<any>(null);
  const [stale, setStale] = useState(false);
  const { selectedPolygonId, setSelectedPolygonId, setSelectedLatLng } = useSelection();
  const { selectedDate } = useDate();
  const { selectedTimezone } = useTimezone();
  const { data: simResults } = useCurrentSimulationResults();
  const { isSimulationComplete, currentSavedSimId, isAnimationPlaying } = useSimulation();
  const { data: simulationsData } = useSimulations();
  const unitSystem = useUnitSystem();
  const [active, setActive] = useState(true);
  const [metric, setMetric] = useState<RiskFromMetric>("fire_size");
  const [hiddenClasses, setHiddenClasses] = useState<number[]>([]);
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);
  const hoveredId = hover?.id ?? null;
  const { map } = useMap();
  // Keep react-query's reference: a fresh `?? []` each render would make the
  // memo below recompute, and the effect that follows it re-run, every render.
  const results = simResults?.data?.results;

  const isEnsemble = Boolean(
    simulationsData?.data?.find((s) => s.simulation_id === currentSavedSimId)?.is_ensemble
  );

  // Turn on the layer whenever a new simulation is loaded
  useEffect(() => {
    if (currentSavedSimId) {
      setActive(true);
    }
  }, [currentSavedSimId]);

  // Class filters belong to one metric's classes; a new metric starts clean.
  useEffect(() => {
    setHiddenClasses([]);
  }, [metric]);

  const metricDef = useMemo(() => riskFromMetric(metric, unitSystem), [metric, unitSystem]);
  const otherMetricDef = useMemo(
    () => riskFromMetric(metric === "fire_size" ? "impacted_assets" : "fire_size", unitSystem),
    [metric, unitSystem]
  );
  const valueOf = useCallback(
    (d: DisplayIgnition) => (metric === "impacted_assets" ? d.asset_impacts : d.ha_burnt),
    [metric]
  );

  // The day's ignitions, collapsed to one entry per location.
  const dayData = useMemo(() => {
    if (!results || results.length === 0 || !isSimulationComplete) return null;
    const selectedDateOnly = selectedDate.format("YYYY-MM-DD");
    const matchingData = results.find(
      (data) =>
        dayjs(data.start_date_time).tz(selectedTimezone).format("YYYY-MM-DD") === selectedDateOnly
    );
    if (!matchingData) return null;
    const ignitions = matchingData.ignitions ?? [];

    // Ensembles replicate the same base ignition points once per member, so
    // mapping ignitions 1:1 stacks M translucent squares per location —
    // rendering them opaque, with the hover value from whichever member is
    // on top. Collapse to one feature per location, averaging the metrics
    // across members. Each feature keeps the FIRST instance's array index
    // so its `ignition-<index>` id still resolves to a valid ignition (and
    // its base point) in the AnimationLayer. Single runs pass through
    // unchanged (one instance per location).
    const byLocation = new Map<string, DisplayIgnition>();
    ignitions.forEach((ig: any, index: number) => {
      const key = `${ig.start_point_lat},${ig.start_point_lng}`;
      const entry = byLocation.get(key);
      if (entry) {
        entry.ha_burnt += ig.ha_burnt ?? 0;
        entry.asset_impacts += ig.asset_impacts ?? 0;
        entry.count += 1;
      } else {
        byLocation.set(key, {
          index,
          ignition: ig,
          ha_burnt: ig.ha_burnt ?? 0,
          asset_impacts: ig.asset_impacts ?? 0,
          count: 1,
        });
      }
    });
    const displayIgnitions = Array.from(byLocation.values()).map((e) => ({
      ...e,
      ha_burnt: e.ha_burnt / e.count,
      asset_impacts: e.asset_impacts / e.count,
    }));

    // Grid cells only when the DISTINCT locations form a lattice — the
    // ensemble replicas above must not count as rows (#283).
    const grid = detectIgnitionGrid({
      latStep: matchingData.lat_step,
      lngStep: matchingData.lng_step,
      ignitions: displayIgnitions.map((d) => ({
        lat: d.ignition.start_point_lat,
        lng: d.ignition.start_point_lng,
      })),
    });

    const shaped = displayIgnitions
      .map((d) => ({
        ...d,
        display: displayGeometry(d.ignition, grid.latStep, grid.lngStep, grid.isGridded),
      }))
      .filter((d) => d.display !== null) as Array<DisplayIgnition & { display: NonNullable<ReturnType<typeof displayGeometry>> }>;

    return { ignitions: shaped, isGridded: grid.isGridded, members: shaped[0]?.count ?? 1 };
  }, [results, isSimulationComplete, selectedDate, selectedTimezone]);

  // On load, the highest-value ignition for the active metric goes into the
  // player. Once per simulation + day: a user who then clicks away is not
  // second-guessed. Ensembles are left on their whole-member view, which is
  // what their player opens on. Deferred a tick so it lands after the
  // Animation layer's own reset of the selection on simulation change.
  const autoSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    // Wait for the simulations list: until it arrives every run looks like
    // a single run, and an ensemble would be auto-selected by mistake.
    if (!simulationsData || !dayData || dayData.ignitions.length === 0 || isEnsemble) return;
    const key = `${currentSavedSimId}|${selectedDate.format("YYYY-MM-DD")}`;
    if (autoSelectedRef.current === key) return;
    autoSelectedRef.current = key;
    if (selectedPolygonId !== null) return;
    const top = dayData.ignitions.reduce((best, d) => (valueOf(d) > valueOf(best) ? d : best));
    const handle = window.setTimeout(() => setSelectedPolygonId(`ignition-${top.index}`), 0);
    return () => window.clearTimeout(handle);
    // Intentionally keyed on the data, not the selection: this runs once per load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayData, isEnsemble, simulationsData, currentSavedSimId]);

  useEffect(() => {
    if (!dayData) {
      if (results && results.length > 0 && isSimulationComplete) {
        setGeojson({ type: "FeatureCollection", features: [] });
        setStale(true);
      }
      return;
    }
    setStale(true); // Set stale to true on data change

    const features: any[] = [];
    dayData.ignitions.forEach((d) => {
      const fid = `ignition-${d.index}`;
      const isSelected = fid === selectedPolygonId;
      const value = valueOf(d);
      const klass = riskFromClass(value, metricDef);
      const color = RISK_FROM_RAMP[klass];
      const hidden = hiddenClasses.includes(klass);
      const dimmed = isAnimationPlaying && !isSelected;
      const kind = d.display.kind;
      const fill =
        kind === "cell"
          ? hexToRgba(color, RISK_FROM_CELL_ALPHA[klass])
          : hexToRgba(color, RISK_FROM_SHAPE_ALPHA);
      const fillHover = kind === "shape" ? hexToRgba(color, RISK_FROM_SHAPE_ALPHA_HOVER) : undefined;

      const otherDef = otherMetricDef;
      const otherValue = metric === "fire_size" ? d.asset_impacts : d.ha_burnt;
      const scenarioPrefix = d.count > 1 ? `Mean of ${d.count} scenarios — ` : "";
      const action = isSelected ? "Loaded in the player" : "Click to load this fire";
      const hoverLabel =
        `${scenarioPrefix}${metricDef.label}: ${metricDef.format(value)} · ` +
        `${otherDef.label}: ${otherDef.format(otherValue)} · ${action}`;

      features.push({
        type: "Feature",
        // Numeric feature id: what feature-state (the hover highlight) keys on.
        id: d.index,
        geometry: d.display.geometry,
        properties: {
          id: fid,
          kind,
          ha_burnt: d.ha_burnt,
          asset_impacts: d.asset_impacts,
          classIndex: klass,
          color,
          fill,
          fillHover,
          radius: RISK_FROM_POINT_RADIUS[klass],
          hoverLabel,
          isSelected,
          hidden,
          dimmed,
        },
      });

      if (isSelected && !hidden) {
        const anchor = anchorOf(d.display.geometry, d.ignition);
        if (anchor) {
          // Offset in ems: clear the disc (points) or sit just off the anchor.
          const dx = kind === "point" ? (RISK_FROM_POINT_RADIUS[klass] + 10) / 12 : 0.8;
          features.push({
            type: "Feature",
            id: CHIP_ID_BASE + d.index,
            geometry: { type: "Point", coordinates: anchor },
            properties: {
              id: `${fid}-chip`,
              kind: "chip" as RiskFromFeatureKind,
              chipState: isAnimationPlaying ? "Playing" : "Loaded",
              chipValue: metricDef.format(value),
              chipOffset: dx,
            },
          });
        }
      }
    });

    setGeojson({ type: "FeatureCollection", features });
  }, [
    dayData,
    results,
    isSimulationComplete,
    selectedPolygonId,
    hiddenClasses,
    isAnimationPlaying,
    metric,
    metricDef,
    otherMetricDef,
    unitSystem,
    valueOf,
  ]);

  // ---- Legend ----
  const legendVariant: RiskFromLegendVariant | null = useMemo(() => {
    if (!dayData || dayData.ignitions.length === 0) return null;
    if (dayData.ignitions.length === 1) return "single";
    if (dayData.isGridded) return "grid";
    return dayData.ignitions.some((d) => d.display.kind === "point") ? "points" : "grid";
  }, [dayData]);

  const classCounts = useMemo(() => {
    const counts = new Array(RISK_FROM_CLASS_COUNT).fill(0);
    dayData?.ignitions.forEach((d) => {
      counts[riskFromClass(valueOf(d), metricDef)] += 1;
    });
    return counts;
  }, [dayData, metricDef, valueOf]);

  const findIgnition = (fid: string | null) =>
    fid ? dayData?.ignitions.find((d) => `ignition-${d.index}` === fid) ?? null : null;
  const selectedIgnition = findIgnition(selectedPolygonId);
  const hoveredIgnition = findIgnition(hoveredId);

  const legend =
    active && isSimulationComplete && legendVariant ? (
      <RiskFromLegend
        key={`${id}-legend`}
        variant={legendVariant}
        metric={metric}
        onMetricChange={setMetric}
        unitSystem={unitSystem}
        counts={classCounts}
        hiddenClasses={hiddenClasses}
        onToggleClass={(k) =>
          setHiddenClasses((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]))
        }
        onShowAll={() => setHiddenClasses([])}
        selected={
          selectedIgnition
            ? {
                value: metricDef.format(valueOf(selectedIgnition)),
                position: riskFromRampPosition(valueOf(selectedIgnition), metricDef),
              }
            : null
        }
        hoverPosition={hoveredIgnition ? riskFromRampPosition(valueOf(hoveredIgnition), metricDef) : null}
        note={dayData && dayData.members > 1 ? `Mean of ${dayData.members} scenarios per ignition` : null}
      />
    ) : null;

  const hoverCard =
    active && isSimulationComplete && hover && hoveredIgnition ? (
      <RiskFromHoverCard
        key={`${id}-hover`}
        x={hover.x}
        y={hover.y}
        containerWidth={map?.getContainer().clientWidth ?? Infinity}
        containerHeight={map?.getContainer().clientHeight ?? Infinity}
        kind={hoveredIgnition.display.kind}
        swatchColor={RISK_FROM_RAMP[riskFromClass(valueOf(hoveredIgnition), metricDef)]}
        primaryLabel={metricDef.label}
        primaryValue={metricDef.format(valueOf(hoveredIgnition))}
        secondaryLabel={otherMetricDef.label}
        secondaryValue={otherMetricDef.format(
          metric === "fire_size" ? hoveredIgnition.asset_impacts : hoveredIgnition.ha_burnt
        )}
        note={hoveredIgnition.count > 1 ? `Mean of ${hoveredIgnition.count} scenarios` : null}
        loaded={hoveredId === selectedPolygonId}
      />
    ) : null;

  const layerStyle = useMemo(() => riskFromLayerStyles(id), [id]);

  const toolbarButton = (
    <ToolbarButton
      key={id}
      id={id}
      name={name}
      toggledOn={active}
      enabled={!!geojson && isSimulationComplete}
      onClick={() => setActive(!active)}
      icon={"unfold_more"}
      labelSide={"top"}
    />
  );

  const handleClick = (e: any) => {
    setSelectedLatLng({ lat: e.lngLat.lat, lng: e.lngLat.lng });

    if (!e.features || e.features.length === 0) {
      setSelectedPolygonId(null);
      return;
    }

    // Filtered-out classes stay on the map at zero opacity (so they keep
    // their place in the layer); they are not clickable.
    const feature = e.features.find((f: any) => !f?.properties?.hidden);
    setSelectedPolygonId(feature?.properties?.id || null);
  };

  // Highlight goes straight to the map as feature-state; React only hears
  // about it when the hovered ignition changes (the card and legend tick),
  // not on every pointer move.
  const hoverStateRef = useRef<number | null>(null);
  const setHoverState = useCallback(
    (featureId: number | null) => {
      if (hoverStateRef.current === featureId) return;
      if (map && map.getSource(id)) {
        if (hoverStateRef.current !== null) {
          map.setFeatureState({ source: id, id: hoverStateRef.current }, { hover: false });
        }
        if (featureId !== null) {
          map.setFeatureState({ source: id, id: featureId }, { hover: true });
        }
      }
      hoverStateRef.current = featureId;
    },
    [map, id]
  );
  useEffect(() => {
    if (!active) setHoverState(null);
  }, [active, setHoverState]);

  const handleHover = (feature: any | null, point: { x: number; y: number }) => {
    const usable = feature && !feature.properties?.hidden;
    const fid: string | null = usable ? feature.properties?.id ?? null : null;
    const numericId = usable && typeof feature.id === "number" ? feature.id : null;
    setHoverState(fid ? numericId : null);
    setHover((prev) => {
      if (!fid) return prev === null ? prev : null;
      // Anchor the card where the pointer entered the ignition.
      if (prev && prev.id === fid) return prev;
      return { id: fid, x: point.x, y: point.y };
    });
  };

  const interactiveLayerIds = active ? riskFromInteractiveLayerIds(id) : [];

  return {
    id: id,
    name: name,
    source: geojson,
    layerStyle,
    toolbarButton: toolbarButton,
    active: active,
    stale: stale,
    setStale: setStale,
    onClick: handleClick,
    onHover: handleHover,
    interactiveLayerIds: interactiveLayerIds,
    customComponent:
      legend || hoverCard ? (
        <>
          {legend}
          {hoverCard}
        </>
      ) : null,
  };
};

export default RiskFromLayer;
